import { readConsentSnapshot } from "./consent";

const ATTRIBUTION_SCHEMA_VERSION = 2;
const ATTRIBUTION_STORAGE_KEY = "water_doctor_attribution_v2";
const LEGACY_ATTRIBUTION_STORAGE_KEY = "water_doctor_ad_attribution_v1";
const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const SHOP_TO_OFFICIAL_HANDOFF = "shop_to_official";
const browserDocumentStates = new WeakMap();

// Document identity changes on a genuine navigation, but not on App Router
// transitions. Keep entry capture and unavailable-storage fallback at that scope.
function getBrowserDocumentState() {
  let state = browserDocumentStates.get(document);
  if (state) return state;

  const memory = new Map();
  const memoryOnlyKeys = new Set();
  let persistentStorage;
  try { persistentStorage = window.localStorage; } catch { /* Storage can be denied by browser policy. */ }
  const storage = {
    getItem(key) {
      if (memoryOnlyKeys.has(key)) return memory.get(key) ?? null;
      try {
        if (persistentStorage) {
          const value = persistentStorage.getItem(key);
          if (value != null) memory.set(key, value);
          else memory.delete(key);
          return value ?? null;
        }
      } catch { /* Use the document-local copy when persistence is unavailable. */ }
      return memory.get(key) ?? null;
    },
    setItem(key, value) {
      memory.set(key, value);
      try {
        persistentStorage?.setItem(key, value);
      } catch {
        // Do not let an older readable value replace a newer failed write.
        memoryOnlyKeys.add(key);
      }
    },
    removeItem(key) {
      memory.delete(key);
      try { persistentStorage?.removeItem(key); } catch { memoryOnlyKeys.add(key); }
    },
  };
  state = { storage, captured: false };
  browserDocumentStates.set(document, state);
  return state;
}

function readBrowserCookies() {
  try { return document.cookie; } catch { return ""; }
}

export function captureBrowserEntryAttribution() {
  if (typeof window === "undefined" || typeof document === "undefined") return undefined;
  const state = getBrowserDocumentState();
  if (!state.captured) {
    // Navigation Timing retains the entry URL even if hydration/capture happens
    // after a client-side route change. A new Document captures its own referrer.
    const entryUrl = window.performance?.getEntriesByType?.("navigation")?.[0]?.name || window.location.href;
    captureAdAttribution(entryUrl, state.storage, {
      referrer: document.referrer,
      cookieHeader: readBrowserCookies(),
    });
    state.captured = true;
  }
  return getStoredAttributionRecord(state.storage);
}

const TRACKING_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "fbclid",
  "twclid",
  "utm_id",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

const PAID_MEDIA = new Set([
  "cpc",
  "ppc",
  "paid",
  "paid_search",
  "paid_social",
  "display",
]);
const SEARCH_SOURCES = new Set([
  "google",
  "bing",
  "yahoo",
  "duckduckgo",
  "ecosia",
  "baidu",
]);
const INTERNAL_UTM_SOURCES = new Set([
  "waterdoctor",
  "waterdoctor.ca",
  "shop_waterdoctor_ca",
  "shop.waterdoctor.ca",
]);
const SOCIAL_MEDIA = new Set([
  "social",
  "organic_social",
  "social_media",
]);
const MAX_VALUE_LENGTH = 500;

function cleanValue(value, maxLength = MAX_VALUE_LENGTH) {
  if (typeof value !== "string") return undefined;
  const cleaned = value.trim();
  return cleaned ? cleaned.slice(0, maxLength) : undefined;
}

function createId(prefix) {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
}

function cleanInternalId(value) {
  const cleaned = cleanValue(value, 100);
  return cleaned && /^[A-Za-z0-9_-]+$/.test(cleaned) ? cleaned : undefined;
}

function getIncomingShopIdentity(parsedUrl) {
  if (parsedUrl.searchParams.get("wd_handoff") !== SHOP_TO_OFFICIAL_HANDOFF) {
    return {};
  }

  return {
    journeyId: cleanInternalId(parsedUrl.searchParams.get("wd_journey_id")),
    leadId: cleanInternalId(parsedUrl.searchParams.get("wd_lead_id")),
  };
}

export function createSubmissionId() {
  return createId("sub");
}

function isWaterDoctorHostname(hostname) {
  const normalized = hostname?.toLowerCase();
  return normalized === "waterdoctor.ca" || normalized?.endsWith(".waterdoctor.ca");
}

function getSanitizedReferrer(referrer, destinationHostname) {
  if (!referrer) return {};

  try {
    const parsed = new URL(referrer);
    if (
      parsed.hostname === destinationHostname ||
      (isWaterDoctorHostname(parsed.hostname) && isWaterDoctorHostname(destinationHostname))
    ) {
      return {};
    }

    return {
      referrerDomain: cleanValue(parsed.hostname.toLowerCase(), 253),
      referrerPath: cleanValue(parsed.pathname || "/", 500),
    };
  } catch {
    return {};
  }
}

function parseCookies(cookieHeader = "") {
  const cookies = {};
  cookieHeader.split(";").forEach((pair) => {
    const separatorIndex = pair.indexOf("=");
    if (separatorIndex === -1) return;

    const name = pair.slice(0, separatorIndex).trim();
    const value = pair.slice(separatorIndex + 1).trim();
    if (name) cookies[name] = value;
  });
  return cookies;
}

function getBrowserIdentifiers(cookieHeader = "") {
  const cookies = parseCookies(cookieHeader);
  const gaCookie = cleanValue(cookies._ga, 150);
  const gaClientParts = gaCookie?.split(".");
  const gaClientId = gaClientParts?.length >= 4
    ? gaClientParts.slice(-2).join(".")
    : undefined;

  const gaSessionCookie = Object.entries(cookies).find(
    ([name]) => name.startsWith("_ga_")
  )?.[1];
  const gaSessionParts = gaSessionCookie?.split(".");
  const gaSessionId = cleanValue(
    gaSessionCookie?.match(/(?:^|[.$])s(\d+)(?:[.$]|$)/)?.[1] ||
      (gaSessionParts?.[0]?.startsWith("GS1") && /^\d+$/.test(gaSessionParts?.[2])
        ? gaSessionParts[2]
        : undefined),
    30
  );

  return {
    gaClientId,
    gaSessionId,
    fbc: cleanValue(cookies._fbc, 500),
    fbp: cleanValue(cookies._fbp, 500),
  };
}

function hasPaidMedium(medium) {
  return PAID_MEDIA.has(medium?.toLowerCase());
}

function platformFromReferrerDomain(domain) {
  const normalized = domain?.toLowerCase() || "";
  const searchMatch = normalized.match(
    /^(?:www\.)?(google|bing|yahoo|duckduckgo|ecosia|baidu)\./
  );
  if (searchMatch) return searchMatch[1];
  if (/(^|\.)facebook\.com$/.test(normalized)) return "facebook";
  if (/(^|\.)instagram\.com$/.test(normalized)) return "instagram";
  if (/(^|\.)(x\.com|twitter\.com|t\.co)$/.test(normalized)) return "x";
  return undefined;
}

function sourceFromReferrerDomain(domain) {
  const platform = platformFromReferrerDomain(domain);
  if (platform === "google") return "GOOGLE_ORGANIC";
  if (SEARCH_SOURCES.has(platform)) return "SEARCH_ENGINE";
  if (["facebook", "instagram", "x"].includes(platform)) return "ORGANIC_SOCIAL";
  return domain ? "WEBSITE_REFERRAL" : "DIRECT";
}

/**
 * Convert tracking evidence into the Dashboard's normalized acquisition source.
 * Browser cookies such as _fbc and _fbp are deliberately excluded from
 * classification: they help match a browser to an earlier click, but they do
 * not prove that the current touch came from an ad.
 */
export function classifyAttributionSource(attribution = {}) {
  const source = cleanValue(
    attribution.utmSource || attribution.utm_source || attribution.sourcePlatform,
    100
  )?.toLowerCase();
  const medium = cleanValue(attribution.medium || attribution.utmMedium || attribution.utm_medium, 100)
    ?.toLowerCase();
  const referrerDomain = attribution.referrerDomain || attribution.referrer_domain;

  if (attribution.gclid || attribution.gbraid || attribution.wbraid || attribution.gadCampaignId || attribution.gad_campaignid) {
    return "GOOGLE_ADS";
  }

  if (attribution.twclid) return "X_ADS";

  if (attribution.fbclid) {
    if (source === "instagram" || source === "ig") return "INSTAGRAM_ADS";
    if (source === "facebook" || source === "fb") return "FACEBOOK_ADS";
    return "META_ADS";
  }

  if (INTERNAL_UTM_SOURCES.has(source)) return sourceFromReferrerDomain(referrerDomain);

  if (source === "instagram" || source === "ig") {
    return hasPaidMedium(medium) ? "INSTAGRAM_ADS" : "ORGANIC_SOCIAL";
  }
  if (source === "facebook" || source === "fb" || source === "meta") {
    return hasPaidMedium(medium) ? (source === "facebook" || source === "fb" ? "FACEBOOK_ADS" : "META_ADS") : "ORGANIC_SOCIAL";
  }
  if (source === "x" || source === "twitter") {
    return hasPaidMedium(medium) ? "X_ADS" : "ORGANIC_SOCIAL";
  }
  if (SEARCH_SOURCES.has(source)) {
    return hasPaidMedium(medium) ? (source === "google" ? "GOOGLE_ADS" : "PAID_SEARCH") : "SEARCH_ENGINE";
  }
  if (medium === "email") return "EMAIL";
  if (SOCIAL_MEDIA.has(medium)) return "SOCIAL_MEDIA";
  if (medium === "referral") return "WEBSITE_REFERRAL";
  if (source && source !== "direct" && source !== "(direct)") return "WEBSITE_REFERRAL";

  return sourceFromReferrerDomain(referrerDomain);
}

function buildTouch(parsedUrl, { capturedAt, referrer, cookieHeader } = {}) {
  const touch = {};
  TRACKING_PARAMS.forEach((key) => {
    const value = cleanValue(parsedUrl.searchParams.get(key));
    if (value) touch[key] = value;
  });

  const { referrerDomain, referrerPath } = getSanitizedReferrer(
    referrer,
    parsedUrl.hostname
  );
  const { fbc, fbp } = getBrowserIdentifiers(cookieHeader);

  const normalizedTouch = {
    sourcePlatform:
      cleanValue(touch.utm_source, 100) ||
      platformFromReferrerDomain(referrerDomain),
    medium: cleanValue(touch.utm_medium, 100),
    campaign: cleanValue(touch.utm_campaign, 200),
    campaignId: cleanValue(touch.utm_id || touch.gad_campaignid, 200),
    term: cleanValue(touch.utm_term, 200),
    content: cleanValue(touch.utm_content, 200),
    landingPage: cleanValue(parsedUrl.pathname || "/", 500),
    referrerDomain,
    referrerPath,
    capturedAt: capturedAt || new Date().toISOString(),
    gclid: touch.gclid,
    gbraid: touch.gbraid,
    wbraid: touch.wbraid,
    gadSource: touch.gad_source,
    gadCampaignId: touch.gad_campaignid,
    fbclid: touch.fbclid,
    fbc: fbc || (touch.fbclid ? `fb.1.${Date.parse(capturedAt || new Date().toISOString())}.${touch.fbclid}` : undefined),
    fbp,
    twclid: touch.twclid,
  };

  normalizedTouch.source = classifyAttributionSource(normalizedTouch);
  return Object.fromEntries(
    Object.entries(normalizedTouch).filter(([, value]) => value !== undefined)
  );
}

export function getAdAttributionFromUrl(
  url,
  capturedAt = new Date().toISOString(),
  { referrer = "", cookieHeader = "" } = {}
) {
  try {
    return buildTouch(new URL(url), { capturedAt, referrer, cookieHeader });
  } catch {
    return null;
  }
}

function readStoredRecord(storage, now = Date.now()) {
  if (!storage) return null;

  try {
    const raw = storage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;

    const record = JSON.parse(raw);
    const updatedAt = Date.parse(record?.updatedAt || record?.firstTouch?.capturedAt);
    if (
      record?.schemaVersion !== ATTRIBUTION_SCHEMA_VERSION ||
      !Number.isFinite(updatedAt) ||
      now - updatedAt > ATTRIBUTION_MAX_AGE_MS
    ) {
      storage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }

    return record;
  } catch {
    return null;
  }
}

function writeStoredRecord(storage, record) {
  if (!storage) return;
  try {
    storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Attribution must never interrupt navigation or a form submission.
  }
}

function migrateLegacyRecord(storage) {
  if (!storage) return null;

  try {
    const raw = storage.getItem(LEGACY_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const legacy = JSON.parse(raw);
    const capturedAt = cleanValue(legacy.captured_at, 50) || new Date().toISOString();
    const touch = {
      sourcePlatform: cleanValue(legacy.utm_source, 100),
      medium: cleanValue(legacy.utm_medium, 100),
      campaign: cleanValue(legacy.utm_campaign, 200),
      campaignId: cleanValue(legacy.utm_id || legacy.gad_campaignid, 200),
      term: cleanValue(legacy.utm_term, 200),
      content: cleanValue(legacy.utm_content, 200),
      landingPage: cleanValue(legacy.landing_page, 500),
      capturedAt,
      gclid: cleanValue(legacy.gclid),
      gbraid: cleanValue(legacy.gbraid),
      wbraid: cleanValue(legacy.wbraid),
      gadCampaignId: cleanValue(legacy.gad_campaignid, 200),
      fbclid: cleanValue(legacy.fbclid),
      fbc: cleanValue(legacy.fbc),
      fbp: cleanValue(legacy.fbp),
      twclid: cleanValue(legacy.twclid),
    };
    touch.source = classifyAttributionSource(touch);

    const record = {
      schemaVersion: ATTRIBUTION_SCHEMA_VERSION,
      journeyId: createId("jny"),
      firstTouch: touch,
      lastNonDirectTouch: touch.source !== "DIRECT" ? touch : undefined,
      updatedAt: capturedAt,
    };
    writeStoredRecord(storage, record);
    storage.removeItem(LEGACY_ATTRIBUTION_STORAGE_KEY);
    return record;
  } catch {
    return null;
  }
}

export function captureAdAttribution(
  url,
  storage,
  { referrer = "", cookieHeader = "", capturedAt = new Date().toISOString() } = {}
) {
  let parsedUrl;
  try {
    parsedUrl = new URL(url);
  } catch {
    return null;
  }

  const touch = buildTouch(parsedUrl, { capturedAt, referrer, cookieHeader });
  if (!touch) return null;

  const existing = readStoredRecord(storage) || migrateLegacyRecord(storage);
  const incomingIdentity = getIncomingShopIdentity(parsedUrl);
  const isNonDirect = touch.source !== "DIRECT" && touch.source !== "UNKNOWN";
  const record = {
    schemaVersion: ATTRIBUTION_SCHEMA_VERSION,
    journeyId: incomingIdentity.journeyId || existing?.journeyId || createId("jny"),
    leadId: incomingIdentity.leadId || cleanInternalId(existing?.leadId),
    firstTouch: existing?.firstTouch || touch,
    lastNonDirectTouch: isNonDirect ? touch : existing?.lastNonDirectTouch,
    updatedAt: isNonDirect || !existing ? capturedAt : existing.updatedAt,
  };

  writeStoredRecord(storage, record);
  return record;
}

export function getStoredAttributionRecord(storage, now = Date.now()) {
  return readStoredRecord(storage, now) || migrateLegacyRecord(storage);
}

export function getAttributionJourneyId(storage) {
  const existing = getStoredAttributionRecord(storage);
  if (existing?.journeyId) return existing.journeyId;

  const now = new Date().toISOString();
  const record = {
    schemaVersion: ATTRIBUTION_SCHEMA_VERSION,
    journeyId: createId("jny"),
    updatedAt: now,
  };
  writeStoredRecord(storage, record);
  return record.journeyId;
}

export function getAttributionLeadId(storage) {
  return cleanInternalId(getStoredAttributionRecord(storage)?.leadId);
}

export function setAttributionLeadId(storage, leadId) {
  const cleanedLeadId = cleanInternalId(leadId);
  const existing = getStoredAttributionRecord(storage);
  if (!cleanedLeadId || !existing) return undefined;

  writeStoredRecord(storage, { ...existing, leadId: cleanedLeadId });
  return cleanedLeadId;
}

// Use the same document-local fallback for forms and Shopify handoffs. Reading
// window.localStorage again here can throw or discard a newer in-memory write.
export function getBrowserAttributionIdentity() {
  const record = captureBrowserEntryAttribution();
  return {
    journeyId: record?.journeyId,
    leadId: cleanInternalId(record?.leadId),
  };
}

export function setBrowserAttributionLeadId(leadId) {
  if (typeof window === "undefined" || typeof document === "undefined") return undefined;
  captureBrowserEntryAttribution();
  return setAttributionLeadId(getBrowserDocumentState().storage, leadId);
}

/**
 * Create the immutable attribution snapshot sent with a single form submission.
 */
export function buildAttributionEnvelope({
  storage,
  url,
  referrer = "",
  cookieHeader = "",
  consent,
  submissionId = createSubmissionId(),
  submittedAt = new Date().toISOString(),
  formType,
  captureCurrentTouch = true,
} = {}) {
  const record = (captureCurrentTouch && captureAdAttribution(url, storage, {
    referrer,
    cookieHeader,
    capturedAt: submittedAt,
  })) || getStoredAttributionRecord(storage);
  const sourceTouch = record?.lastNonDirectTouch || record?.firstTouch || {
    source: "DIRECT",
    capturedAt: submittedAt,
  };
  const identifiers = getBrowserIdentifiers(cookieHeader);

  return {
    schemaVersion: ATTRIBUTION_SCHEMA_VERSION,
    journeyId: record?.journeyId || getAttributionJourneyId(storage),
    submissionId,
    submissionPage: (() => {
      try { return cleanValue(new URL(url).pathname || "/", 500); } catch { return undefined; }
    })(),
    submittedAt,
    formType: cleanValue(formType, 100),
    gaClientId: identifiers.gaClientId,
    gaSessionId: identifiers.gaSessionId,
    consent: consent
      ? {
          analytics: consent.analytics === true,
          advertising: consent.advertising === true,
          capturedAt: cleanValue(consent.decidedAt, 50),
        }
      : undefined,
    firstTouch: record?.firstTouch || sourceTouch,
    lastNonDirectTouch: record?.lastNonDirectTouch,
    submissionTouch: {
      ...sourceTouch,
      fbc: sourceTouch.fbc || identifiers.fbc,
      fbp: sourceTouch.fbp || identifiers.fbp,
      sourceCapturedAt: sourceTouch.capturedAt,
    },
  };
}

export function buildBrowserAttributionEnvelope({ submissionId, formType } = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") return undefined;

  captureBrowserEntryAttribution();
  return buildAttributionEnvelope({
    storage: getBrowserDocumentState().storage,
    url: window.location.href,
    // The entry was captured above. document.referrer still describes that
    // entry after SPA navigation and must not be recorded as a new touch.
    captureCurrentTouch: false,
    cookieHeader: readBrowserCookies(),
    consent: readConsentSnapshot(),
    submissionId: submissionId || createSubmissionId(),
    formType,
  });
}

function sanitizeTouch(touch) {
  if (!touch || typeof touch !== "object" || Array.isArray(touch)) return undefined;

  const sanitized = {
    sourcePlatform: cleanValue(touch.sourcePlatform, 100),
    medium: cleanValue(touch.medium, 100),
    campaign: cleanValue(touch.campaign, 200),
    campaignId: cleanValue(touch.campaignId, 200),
    term: cleanValue(touch.term, 200),
    content: cleanValue(touch.content, 200),
    landingPage: cleanValue(touch.landingPage, 500),
    submissionPage: cleanValue(touch.submissionPage, 500),
    referrerDomain: cleanValue(touch.referrerDomain, 253),
    referrerPath: cleanValue(touch.referrerPath, 500),
    capturedAt: cleanValue(touch.capturedAt, 50),
    sourceCapturedAt: cleanValue(touch.sourceCapturedAt, 50),
    gclid: cleanValue(touch.gclid),
    gbraid: cleanValue(touch.gbraid),
    wbraid: cleanValue(touch.wbraid),
    gadSource: cleanValue(touch.gadSource, 100),
    gadCampaignId: cleanValue(touch.gadCampaignId, 200),
    fbclid: cleanValue(touch.fbclid),
    fbc: cleanValue(touch.fbc),
    fbp: cleanValue(touch.fbp),
    twclid: cleanValue(touch.twclid),
  };
  sanitized.source = classifyAttributionSource(sanitized);
  return Object.fromEntries(
    Object.entries(sanitized).filter(([, value]) => value !== undefined)
  );
}

/**
 * Server-safe allowlist for the client-provided envelope. Source is always
 * recomputed from evidence rather than trusted from the request body.
 */
export function sanitizeAttributionEnvelope(envelope) {
  if (!envelope || typeof envelope !== "object" || Array.isArray(envelope)) return undefined;

  const consent = envelope.consent && typeof envelope.consent === "object"
    ? {
        analytics: envelope.consent.analytics === true,
        advertising: envelope.consent.advertising === true,
        capturedAt: cleanValue(envelope.consent.capturedAt, 50),
      }
    : undefined;

  const sanitized = {
    schemaVersion: ATTRIBUTION_SCHEMA_VERSION,
    journeyId: cleanValue(envelope.journeyId, 100),
    submissionId: cleanValue(envelope.submissionId, 100),
    submissionPage: cleanValue(envelope.submissionPage, 500),
    submittedAt: cleanValue(envelope.submittedAt, 50),
    formType: cleanValue(envelope.formType, 100),
    gaClientId: cleanValue(envelope.gaClientId, 100),
    gaSessionId: cleanValue(envelope.gaSessionId, 50),
    firstTouch: sanitizeTouch(envelope.firstTouch),
    lastNonDirectTouch: sanitizeTouch(envelope.lastNonDirectTouch),
    submissionTouch: sanitizeTouch(envelope.submissionTouch),
    consent,
  };

  return Object.fromEntries(
    Object.entries(sanitized).filter(([, value]) => value !== undefined)
  );
}

// Backward-compatible helper for older callers while they migrate to envelopes.
export function getStoredAdAttribution(storage, now = Date.now()) {
  const record = getStoredAttributionRecord(storage, now);
  return record?.lastNonDirectTouch || record?.firstTouch || null;
}

export function isGoogleAdsAttribution(attribution) {
  return classifyAttributionSource(attribution) === "GOOGLE_ADS";
}

export { ATTRIBUTION_SCHEMA_VERSION, ATTRIBUTION_STORAGE_KEY };
